import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { workspaceAPI, authAPI } from '../lib/api';
import { useAuth } from './AuthContext';
import { WorkspaceLiveProvider } from '../lib/WorkspaceLiveProvider';

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const { user, token } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [workspaceRole, setWorkspaceRole] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchWorkspaces = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await workspaceAPI.list();
      setWorkspaces(res.data);
      if (res.data.length > 0 && !currentWorkspace) {
        const saved = localStorage.getItem('stellar_workspace_id');
        const match = res.data.find(w => w.id === saved);
        setCurrentWorkspace(match || res.data[0]);
      }
    } catch (err) {
      console.error('Failed to fetch workspaces', err);
    } finally {
      setLoading(false);
    }
  }, [token, currentWorkspace]);

  // Fetch workspace role when workspace changes
  useEffect(() => {
    if (currentWorkspace?.id && token) {
      authAPI.getMyWorkspaceRole(currentWorkspace.id)
        .then(res => setWorkspaceRole(res.data.role))
        .catch(() => setWorkspaceRole(user?.role || 'team'));
    }
  }, [currentWorkspace?.id, token, user?.role]);

  useEffect(() => {
    if (user && token) {
      fetchWorkspaces();
    }
  }, [user, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchWorkspace = (ws) => {
    setCurrentWorkspace(ws);
    localStorage.setItem('stellar_workspace_id', ws.id);
  };

  const createWorkspace = async (data) => {
    const res = await workspaceAPI.create(data);
    setWorkspaces(prev => [...prev, res.data]);
    setCurrentWorkspace(res.data);
    localStorage.setItem('stellar_workspace_id', res.data.id);
    return res.data;
  };

  return (
    <WorkspaceContext.Provider value={{
      workspaces, currentWorkspace, workspaceRole, loading,
      switchWorkspace, createWorkspace, fetchWorkspaces
    }}>
      {/* D84 — Workspace live sync provider. Mounted inside the workspace
          context so it has access to currentWorkspace.id + auth token.
          When workspace is null (pre-load or no workspace), the provider
          stays in 'disconnected' state and useLiveData consumers gracefully
          poll. */}
      <WorkspaceLiveProvider workspaceId={currentWorkspace?.id || null} token={token || null}>
        {children}
      </WorkspaceLiveProvider>
    </WorkspaceContext.Provider>
  );
}

export const useWorkspace = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be inside WorkspaceProvider');
  return ctx;
};
